/**
 * Client-side SQL SELECT parser for instant field extraction.
 *
 * NOTE: This module intentionally mirrors the backend's
 * `backend/shared/sql/sql_field_extractor.py` (`extract_fields_from_sql`).
 * The client parser provides instant UI feedback while the backend remains
 * authoritative for persistence. When modifying parsing logic here, check
 * the backend counterpart to avoid drift between the two implementations.
 */
import type { SqlField } from './types';
import {
  QUANTILE_AGGREGATIONS,
  SQL_AGGREGATION_FUNCTIONS,
} from '../../../generated/chartAggregations';

const AGG_FUNCTIONS = new Set<string>(SQL_AGGREGATION_FUNCTIONS);

const AGG_RE = new RegExp(
  `\\b(${[...AGG_FUNCTIONS].join('|')})\\s*\\(`,
  'i',
);

/**
 * Parse SELECT clause from SQL and extract output fields.
 *
 * Returns an array of SqlField with name, type, alias, and aggregation.
 */
export function parseSelectFields(sql: string): SqlField[] {
  // Extract portion between SELECT and FROM
  const m = sql.match(/\bSELECT\s+([\s\S]*?)\s+FROM\b/i);
  if (!m) return [];

  const selectClause = m[1].trim();
  if (selectClause === '*') return [];

  const expressions = splitExpressions(selectClause);
  return expressions.map((expr) => {
    const { rawExpr, alias } = extractAlias(expr);
    const type = detectFieldType(rawExpr);
    const aggregation = detectAggregation(rawExpr);
    return { name: alias, type, alias, aggregation };
  });
}

// ── Helpers ──────────────────────────────────────────────────────

function splitExpressions(clause: string): string[] {
  const expressions: string[] = [];
  let depth = 0;
  let current = '';

  for (const char of clause) {
    if (char === '(') { depth++; current += char; }
    else if (char === ')') { depth--; current += char; }
    else if (char === ',' && depth === 0) {
      expressions.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  const trailing = current.trim();
  if (trailing) expressions.push(trailing);
  return expressions;
}

function extractAlias(expr: string): { rawExpr: string; alias: string } {
  // AS "quoted"
  let m = expr.match(/^(.*?)\s+AS\s+"([^"]+)"\s*$/i);
  if (m) return { rawExpr: m[1].trim(), alias: m[2] };

  // AS unquoted
  m = expr.match(/^(.*?)\s+AS\s+(\w+)\s*$/i);
  if (m) return { rawExpr: m[1].trim(), alias: m[2] };

  // Plain column
  m = expr.trim().match(/^"?(\w+)"?$/);
  if (m) return { rawExpr: expr.trim(), alias: m[1] };

  // Qualified column
  m = expr.trim().match(/^(?:"?\w+"?\.)?"?(\w+)"?$/);
  if (m) return { rawExpr: expr.trim(), alias: m[1] };

  // Fallback
  const clean = expr.trim().replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 30);
  return { rawExpr: expr.trim(), alias: clean || 'expr' };
}

function detectFieldType(expr: string): 'dimension' | 'measure' | 'calculated' {
  if (AGG_RE.test(expr)) return 'measure';
  if (/\bCASE\b/i.test(expr)) return 'calculated';
  if (/[+\-*/]/.test(expr) && !/^"?\w+"?$/.test(expr.trim())) return 'calculated';
  return 'dimension';
}

function detectAggregation(expr: string): string | undefined {
  const m = expr.match(/^\s*(\w+)\s*\(/);
  if (m && AGG_FUNCTIONS.has(m[1].toLowerCase())) {
    const func = m[1].toLowerCase();
    if (func === 'count' && /^\s*count\s*\(\s*distinct\b/i.test(expr)) {
      return 'count_distinct';
    }
    if (func === 'quantile_cont') {
      const quantile = expr.match(/,\s*(0(?:\.\d+)?|1(?:\.0+)?)\s*\)/);
      if (quantile) {
        const value = Number(quantile[1]);
        return Object.entries(QUANTILE_AGGREGATIONS)
          .find(([, expected]) => value === expected)?.[0] ?? func;
      }
    }
    return func;
  }
  return undefined;
}